import type Konva from 'konva';

export type ExportFormat = 'png' | 'jpeg';

interface ExportOptions {
  /** Skala względem docelowego formatu: 1 = dokładny rozmiar, 2 = wersja retina. */
  scale: number;
  /** Aktualne powiększenie podglądu, które trzeba wyzerować przy eksporcie. */
  zoom: number;
  format: ExportFormat;
  fileName: string;
}

const MIME: Record<ExportFormat, string> = {
  png: 'image/png',
  jpeg: 'image/jpeg',
};

/** Nazwa pliku bez znaków, które psują pobieranie na Windowsie. */
export const buildFileName = (parts: string[], format: ExportFormat): string => {
  const slug = parts
    .join('-')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return `${slug || 'kreacja'}.${format === 'jpeg' ? 'jpg' : 'png'}`;
};

/**
 * Uruchamia pobranie pliku pod wskazaną nazwą.
 *
 * Plik idzie jako adres `data:`, NIE `blob:`. Przy `blob:` przeglądarka
 * potrafi zignorować atrybut `download` i zapisać plik pod identyfikatorem
 * adresu (długi ciąg szesnastkowy, bez rozszerzenia) — dokładnie to zjawisko
 * widzieliśmy w historii pobierania. Adres `data:` nie ma ścieżki, z której
 * dałoby się wyprowadzić inną nazwę, więc nazwa z atrybutu zawsze wygrywa.
 */
const triggerDownload = (fileUrl: string, fileName: string): void => {
  const link = document.createElement('a');
  link.href = fileUrl;
  link.setAttribute('download', fileName);
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();

  // Kotwica musi zostać w dokumencie chwilę po kliknięciu: usunięta od razu
  // gubi powiązanie z atrybutem `download`.
  setTimeout(() => link.remove(), 1000);
};

/**
 * Zrzuca planszę do pliku w rozdzielczości formatu, niezależnie od tego, jak
 * mocno podgląd jest pomniejszony. `pixelRatio` odwraca skalę podglądu, więc
 * plansza 1080x1920 zapisze się dokładnie w tym rozmiarze.
 */
export const downloadStage = async (stage: Konva.Stage, options: ExportOptions): Promise<void> => {
  if (document.fonts?.ready) {
    await document.fonts.ready;
  }

  let dataUrl: string;

  try {
    dataUrl = stage.toDataURL({
      x: 0,
      y: 0,
      width: stage.width(),
      height: stage.height(),
      mimeType: MIME[options.format],
      quality: options.format === 'jpeg' ? 0.92 : 1,
      pixelRatio: options.scale / options.zoom,
    });
  } catch (error) {
    // Płótno „skażone" obrazkiem wczytanym bez zgody CORS blokuje eksport
    // CAŁEJ kreacji, nie tylko tego jednego zdjęcia.
    if (error instanceof DOMException && error.name === 'SecurityError') {
      throw new Error(
        'Przeglądarka zablokowała eksport, bo któreś zdjęcie w kreacji zostało wczytane bez zgody na użycie '
        + 'w pliku. Wstaw je ponownie z biblioteki albo pobierz wersję z licencją.',
      );
    }
    throw error;
  }

  triggerDownload(dataUrl, options.fileName);
};
